import { ChangeDetectorRef, Component, HostListener, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { Pagination, Wheel, WheelFiltersResponse } from '../../models/response.model';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { ProductDataService } from '../../services/product-data.service';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { Store } from '@ngxs/store';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';

@Component({
  selector: 'app-wheels',
  templateUrl: './wheels.component.html',
  styleUrls: ['./wheels.component.scss'],
  standalone: false
})
export class WheelsComponent implements OnInit, CanComponentDeactivate {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('deleteConfirmation', { static: true }) deleteModal: ModalComponent;
  @ViewChild('deleteContent') deleteContent: TemplateRef<any>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;

  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  wheelList: Wheel[] = [];
  wheelCodeFilterList: string[] = [];
  wheelDescriptionFilterList: string[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  isSaveEnabled = false;
  locallyAddedWheels: Wheel[] = [];
  itemToDelete: Wheel | null = null;
  productSaveDisabled = true;
  private controlMap = new Map<string, Map<string, FormControl>>();

  pagination: Pagination;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [5, 10, 50, 100];

  tableConfig: TableConfig<Wheel> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };

  fetcher: PaginatorFetcher<Wheel> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();

    return this.productDataService.getWheels({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        const { wheels = [], pagination } = response.data || {};
        this.wheelList = wheels;
        this.pagination = pagination;
        const combined = [...this.locallyAddedWheels, ...this.wheelList];
        this.updateDisplayedRowsForm(combined);
        const totalCount = (pagination?.totalRecords || wheels.length) + this.locallyAddedWheels.length;
        return {
          items: combined,
          count: totalCount
        };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      wheelCodes: [[]],
      wheelDescriptions: [[]]
    });
    this.filterFormBackend = this.fb.group({
      wheelCodes: [[]],
      wheelDescriptions: [[]]
    });
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      wheelCodes: this.filterForm.get('wheelCodes')?.value || '',
      wheelDescriptions: this.filterForm.get('wheelDescriptions')?.value || ''
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.setupFormListeners();
    this.loadWheelFilters();
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.checkIfModified();
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        wheelCode: {
          cell: 'wheelCode',
          header: this.translationService.translate('wheelCodeText') || 'Wheel Code'
        },
        wheelDescription: {
          cell: 'wheelDescription',
          header: this.translationService.translate('wheelDescriptionText') || 'Wheel Description'
        },
        action: {
          cell: 'action',
          header: this.translationService.translate('actionText') || 'Action'
        }
      };
      this.cdr.markForCheck();
    });
  }

  private loadWheelFilters(): void {
    this.productDataService.getWheelFilters().subscribe((response: WheelFiltersResponse) => {
      this.wheelCodeFilterList = response.data.wheelCodes || [];
      this.wheelDescriptionFilterList = response.data.wheelDescriptions || [];
    });
  }

  resetFilters(): void {
    this.filterForm.reset();
  }

  private setupFormListeners(): void {
    this.filterForm.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe(() => {
      this.generateFilterForm();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    });
  }

  updateDisplayedRowsForm(items: Wheel[]): void {
    // Snapshot current form values to preserve unsaved user edits
    const currentValues = new Map<string, any>();
    const existingFormArray = this.displayedRowsForm.get('rows') as FormArray;
    if (existingFormArray) {
      for (let i = 0; i < existingFormArray.length; i++) {
        const val = existingFormArray.at(i).value;
        if (val?.wheelPk) {
          currentValues.set(val.wheelPk, val);
        }
      }
    }

    this.controlMap.clear();
    const rowForms = items.map((row) => {
      // Use previously edited values if available (for unsaved local rows)
      const editedValues = currentValues.get(row.wheelPk);
      const rowData = editedValues ? { ...row, ...editedValues } : row;
      const group = this.createRowForm(rowData);
      const key = row.wheelPk || '';
      const fieldMap = new Map<string, FormControl>();
      fieldMap.set('wheelPk', group.get('wheelPk') as FormControl);
      fieldMap.set('wheelCode', group.get('wheelCode') as FormControl);
      fieldMap.set('wheelDescription', group.get('wheelDescription') as FormControl);
      this.controlMap.set(key, fieldMap);
      return group;
    });
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  createRowForm(row: Wheel): FormGroup {
    return this.fb.group({
      wheelPk: [row.wheelPk],
      wheelCode: [row.wheelCode, [Validators.required]],
      wheelDescription: [row.wheelDescription, [Validators.required]]
    });
  }

  getControl(item: Wheel, controlName: keyof Wheel): FormControl {
    const key = item?.wheelPk || '';
    const fieldMap = this.controlMap.get(key);
    if (fieldMap) {
      const control = fieldMap.get(controlName as string);
      if (control) {
        return control;
      }
    }

    // Fallback to index-based lookup
    const combinedItems = [...this.locallyAddedWheels, ...this.wheelList];
    const index = combinedItems.findIndex((wheel) => wheel.wheelPk === item.wheelPk);

    if (index === -1) {
      return new FormControl('');
    }

    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (!formArray || formArray.length <= index) {
      return new FormControl('');
    }

    const formGroup = formArray.at(index);
    if (!formGroup) {
      return new FormControl('');
    }

    const control = formGroup.get(controlName as string) as FormControl;
    return control || new FormControl('');
  }

  get canEditWheel(): boolean {
    const roles: string = localStorage.getItem('roles') || '';
    return roles.includes('SETUP_SERVICES_EDIT') && !this.productSaveDisabled;
  }

  checkIfModified(): void {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    const combinedItems = [...this.locallyAddedWheels, ...this.wheelList];
    let modified = false;

    for (let i = 0; i < combinedItems.length; i++) {
      const original = combinedItems[i];
      const current = formArray.at(i)?.value;
      if (!current) continue;
      const hasData = current.wheelCode || current.wheelDescription;
      if (hasData && Object.keys(current).some((key) => current[key] !== original[key])) {
        modified = true;
        break;
      }
    }
    this.isSaveEnabled = modified && this.canEditWheel && !this.productSaveDisabled;
  }

  private validateWheelCode(current: any, index: number, allCurrentValues: any[]): { isValid: boolean; errorMessage?: string } {
    if (!current.wheelCode) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('wheelCodeRequiredText') || 'Wheel Code is required.'
      };
    }

    const duplicateCode = allCurrentValues.find(
      (item) => item.index !== index && item.wheelCode && item.wheelCode.toLowerCase() === current.wheelCode.toLowerCase()
    );
    if (duplicateCode) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('duplicateWheelCodeText') || 'Wheel Code must be unique.'
      };
    }

    return { isValid: true };
  }

  private validateWheelDescription(current: any, index: number, allCurrentValues: any[]): { isValid: boolean; errorMessage?: string } {
    if (!current.wheelDescription) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('wheelDescriptionRequiredText') || 'Wheel Description is required.'
      };
    }

    const duplicateDescription = allCurrentValues.find(
      (item) =>
        item.index !== index && item.wheelDescription && item.wheelDescription.toLowerCase() === current.wheelDescription.toLowerCase()
    );
    if (duplicateDescription) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('duplicateWheelDescriptionText') || 'Wheel Description must be unique.'
      };
    }

    return { isValid: true };
  }

  save(): void {
    if (!this.canEditWheel || !this.isSaveEnabled) {
      return;
    }

    const modifiedRows: Wheel[] = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    const allFormValues = formArray.value.map((val: any, idx: number) => ({ ...val, index: idx }));

    for (let i = 0; i < formArray.length; i++) {
      const current = formArray.at(i).value;

      if (!current.wheelCode && !current.wheelDescription) {
        continue;
      }

      const isValidCode = this.validateWheelCode(current, i, allFormValues);

      if (!isValidCode.isValid) {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: isValidCode.errorMessage!
        } as Alert);
        return;
      }

      const isValidDescription = this.validateWheelDescription(current, i, allFormValues);

      if (!isValidDescription.isValid) {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: isValidDescription.errorMessage!
        } as Alert);
        return;
      }
    }

    const totalRows = [...this.locallyAddedWheels, ...this.wheelList];
    for (let i = 0; i < totalRows.length; i++) {
      const original = totalRows[i];
      const current = formArray.at(i).value;

      const isModified = Object.keys(current).some((key) => current[key] !== original[key]);
      if (isModified) {
        modifiedRows.push({ ...original, ...current });
      }
    }

    if (modifiedRows.length > 0) {
      this.productDataService.updateWheels(modifiedRows).subscribe({
        next: (response: any) => {
          const responseData = response?.data || response;
          if (Array.isArray(responseData)) {
            responseData.forEach((updatedWheel: Wheel) => {
              const existingIndex = this.wheelList.findIndex((wheel) => wheel.wheelPk === updatedWheel.wheelPk);
              if (existingIndex !== -1) {
                this.wheelList[existingIndex] = updatedWheel;
              } else {
                this.wheelList.push(updatedWheel);
              }
            });
          }

          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('wheelSavedText') || 'Wheels saved successfully'
          } as Alert);

          this.locallyAddedWheels = [];
          this.loadWheelFilters();
          this.tableRef.resetFilters();
          this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
          this.checkIfModified();
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('wheelSaveErrorText') || 'Error encountered while saving Wheels'
          } as Alert);
        }
      });
    }
  }

  addRow(): void {
    this.productDataService.getNewWheelPk().subscribe({
      next: (response: any) => {
        const newId = response?.data;
        const newRow: Wheel = {
          wheelPk: newId,
          wheelCode: '',
          wheelDescription: ''
        };
        this.locallyAddedWheels.push(newRow);
        this.isSaveEnabled = true;
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  deleteRow(item: Wheel): void {
    if (this.productSaveDisabled) {
      return;
    }
    const locallyAddedIndex = this.locallyAddedWheels.findIndex((wheel) => wheel.wheelPk === item.wheelPk);

    if (locallyAddedIndex !== -1) {
      this.locallyAddedWheels.splice(locallyAddedIndex, 1);

      this.alertService.showAlert({
        type: AlertType.success,
        content: this.translationService.translate('wheelDeleteText') || 'Wheel Deleted Successfully'
      } as Alert);

      this.updateDisplayedRowsForm([...this.locallyAddedWheels, ...this.wheelList]);
      this.tableRef.resetFilters();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      this.checkIfModified();

      return;
    }
    const payload = { ...item }.wheelPk;
    this.productDataService.deleteWheel(payload).subscribe({
      next: () => {
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('wheelDeleteText') || 'Wheel Deleted Successfully'
        } as Alert);
        this.wheelList = this.wheelList.filter((row) => row.wheelPk !== item.wheelPk);
        this.updateDisplayedRowsForm([...this.locallyAddedWheels, ...this.wheelList]);
        this.loadWheelFilters();
        this.resetFilters();
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
        this.isSaveEnabled = false;
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('wheelDeleteErrorText') || 'Error encountered while deleting the Wheel'
        } as Alert);
      }
    });
  }

  showDeleteModal(item: Wheel): void {
    this.deleteModal.open({
      id: 'deleteConfirmation',
      title: this.translationService.translate('warningText'),
      width: 550,
      content: this.deleteContent,
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText')
    });
    this.itemToDelete = item;
  }

  onDeleteModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'deleteConfirmation' && result.result) {
        this.deleteRow(this.itemToDelete);
      } else {
        this.itemToDelete = null;
      }
    }
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.locallyAddedWheels = [];
        this.updateDisplayedRowsForm(this.wheelList);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
        this.isSaveEnabled = false;
      }
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.isSaveEnabled) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.isSaveEnabled) {
      $event.returnValue = this.translationService.translate('discardChangesText');
    }
  }
}
